import {
	Alert,
	Button,
	Group,
	Loader,
	Paper,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
	addPartnerDomain,
	createCorporatePartner,
	deleteCorporatePartner,
	listCorporatePartners,
	partnerKeys,
	removePartnerDomain,
	updateCorporatePartner,
} from "@/features/partners/admin-partners";
import { toPartnerApiDomain } from "@/features/partners/normalize-partner-domain";
import {
	type PartnerEditor,
	PartnerEditorModal,
	type PartnerPendingDelete,
	partnerDomainOwnerId,
} from "@/features/partners/partner-editor";
import type { PartnerEditorValues } from "@/features/partners/partner-schema";
import { PartnersTable } from "@/features/partners/partners-table";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { getMutationErrorMessage } from "@/shared/api/errors";
import type { PageLimit } from "@/shared/api/types";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

export function PartnersPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit] = useState<PageLimit>(20);
	const [editor, setEditor] = useState<PartnerEditor | null>(null);
	const [pendingDelete, setPendingDelete] =
		useState<PartnerPendingDelete | null>(null);

	const params = {
		search: search.trim().length >= 3 ? search.trim() : undefined,
		page,
		limit,
	};

	const listQuery = useQuery({
		queryKey: partnerKeys.list(params),
		queryFn: () => listCorporatePartners(params),
	});

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: partnerKeys.all });

	const saveMutation = useMutation({
		mutationFn: async (values: PartnerEditorValues) => {
			if (editor?.mode === "edit") {
				return updateCorporatePartner(editor.row.id, {
					name: values.name.trim(),
					isActive: values.isActive,
				});
			}
			const domains = values.domainsText
				.split(/[\s,;]+/)
				.map(toPartnerApiDomain)
				.filter(Boolean);
			return createCorporatePartner({ name: values.name.trim(), domains });
		},
		onSuccess: async () => {
			notifications.show({ color: "green", message: t("partners.saved") });
			closeEditor();
			await invalidate();
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("partners.saveFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteCorporatePartner,
		onSuccess: async () => {
			setPendingDelete(null);
			notifications.show({
				color: "red",
				message: t("partners.partnerDeleted"),
			});
			closeEditor();
			await invalidate();
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("partners.deleteFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const domainMutation = useMutation({
		mutationFn: async ({
			action,
			domain,
			domainId,
		}:
			| { action: "add"; domain: string; domainId?: never }
			| { action: "remove"; domain?: never; domainId: string }) => {
			const id = partnerDomainOwnerId(editor);
			if (!id) {
				return;
			}
			if (action === "add") {
				return addPartnerDomain(id, domain);
			}
			await removePartnerDomain(id, domainId);
		},
		onSuccess: async (row) => {
			setPendingDelete(null);
			if (row && editor?.mode === "edit") {
				setEditor({ mode: "edit", row });
			}
			await invalidate();
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("partners.saveFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const closeEditor = () => setEditor(null);

	const editingRow =
		editor?.mode === "edit"
			? (listQuery.data?.data.find((row) => row.id === editor.row.id) ??
				editor.row)
			: null;

	return (
		<Stack gap="md">
			<Group justify="space-between" align="flex-start">
				<div>
					<Title order={2}>{t("partners.title")}</Title>
					<Text c="dimmed" size="sm">
						{t("partners.subtitle")}
					</Text>
				</div>
				<Button onClick={() => setEditor({ mode: "create" })}>
					{t("partners.addPartner")}
				</Button>
			</Group>

			<Paper p="md" radius="lg" withBorder>
				<TextInput
					mb="md"
					placeholder={t("partners.searchPlaceholder")}
					value={search}
					onChange={(event) => {
						setSearch(event.currentTarget.value);
						setPage(1);
					}}
				/>
				{listQuery.isLoading ? (
					<Group justify="center" py="xl">
						<Loader />
					</Group>
				) : null}
				{listQuery.error ? (
					<Alert color="red" title={t("common.loadError")}>
						{describeApiErrorSync(listQuery.error)}
					</Alert>
				) : null}
				{listQuery.data ? (
					<PartnersTable
						rows={listQuery.data.data}
						page={page}
						hasNextPage={listQuery.data.hasNextPage}
						onPageChange={setPage}
						onEdit={(row) => setEditor({ mode: "edit", row })}
					/>
				) : null}
			</Paper>

			<PartnerEditorModal
				key={
					editor?.mode === "edit" ? editor.row.id : editor ? "create" : "closed"
				}
				editor={editor}
				editingRow={editingRow}
				saving={saveMutation.isPending}
				domainPending={domainMutation.isPending}
				onAddDomain={(domain) =>
					domainMutation.mutate({ action: "add", domain })
				}
				onRequestDeleteDomain={(id, name) =>
					setPendingDelete({ kind: "domain", id, name })
				}
				onRequestDeletePartner={(id, name) =>
					setPendingDelete({ kind: "partner", id, name })
				}
				onClose={closeEditor}
				onSave={(values) => saveMutation.mutate(values)}
			/>

			<ConfirmDialog
				opened={Boolean(pendingDelete)}
				title={
					pendingDelete?.kind === "domain"
						? t("partners.confirmDeleteDomainTitle")
						: t("partners.confirmDeletePartnerTitle")
				}
				body={
					pendingDelete?.kind === "domain"
						? t("partners.confirmDeleteDomainBody", {
								name: pendingDelete.name,
							})
						: t("partners.confirmDeletePartnerBody", {
								name: pendingDelete?.name ?? "",
							})
				}
				loading={deleteMutation.isPending || domainMutation.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={() => {
					if (pendingDelete?.kind === "domain") {
						domainMutation.mutate({
							action: "remove",
							domainId: pendingDelete.id,
						});
						return;
					}
					if (pendingDelete) {
						deleteMutation.mutate(pendingDelete.id);
					}
				}}
			/>
		</Stack>
	);
}
